import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { Stats } from '@sorare/core/src/lib/scoring';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';

import { DecisiveScoreGauge } from '../DecisiveScoreGauge';
import { GAME_SCORE_TO_SPORT, isCategoryDecisive, statToLabel } from '../utils';
import type { DecisiveScore_playerGameScoreInterface } from './__generated__/index.graphql';

const StatLabel = styled(BodyS)`
  &::first-letter {
    text-transform: uppercase;
  }
  flex: 1;
`;

const Score = styled(BodyS)`
  min-width: var(--quadruple-unit);
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

const ScoreRow = styled(Horizontal)`
  gap: var(--unit);
  padding: 0 var(--double-unit);
`;

const DetailsBlock = styled(Vertical)`
  padding: var(--double-unit) 0;
  background: var(--c-nd-100-opaque);
  border-radius: var(--double-unit);
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--c-nd-150);
`;

const GaugeWrapper = styled.div`
  padding: 0 var(--double-unit);
`;

type Props = {
  playerGameScore: DecisiveScore_playerGameScoreInterface;
};

export const DecisiveScore = ({ playerGameScore }: Props) => {
  const { formatNumber } = useIntlContext();
  const decisiveScore =
    isType(playerGameScore, 'PlayerGameScore') ||
    isType(playerGameScore, 'So5Score')
      ? playerGameScore.decisiveScore?.totalScore
      : null;

  const decisiveStats = playerGameScore.detailedScore.filter(stat =>
    isCategoryDecisive(stat.category)
  );

  const { color } = findThresholdColor(
    decisiveScore ?? 0,
    GAME_SCORE_TO_SPORT[playerGameScore.__typename],
    'CARD_SCORE'
  );
  return (
    <Vertical>
      <Horizontal>
        <BodyM bold>
          <FormattedMessage {...fantasy.decisiveScore} />
        </BodyM>
        <BodyM as="p" bold color={color}>
          <FormattedMessage
            {...fantasy.pointsWithStyledScoreWithoutFormat}
            values={{
              score: formatNumber(Number(decisiveScore?.toFixed(2))),
              span: chunks => <span>{chunks}</span>,
            }}
          />
        </BodyM>
      </Horizontal>
      <DetailsBlock gap={2}>
        <GaugeWrapper>
          <DecisiveScoreGauge
            score={decisiveScore ?? 0}
            sport={GAME_SCORE_TO_SPORT[playerGameScore.__typename]}
          />
        </GaugeWrapper>
        {decisiveStats.length > 0 && <Divider />}
        {decisiveStats?.map(stat => {
          const statLabel = statToLabel(stat.stat);

          return (
            <ScoreRow key={stat.stat}>
              <StatIcon statKey={stat.stat as Stats} />
              <StatLabel as="p" color="var(--c-nd-600)">
                {typeof statLabel === 'string' ? (
                  statLabel
                ) : (
                  <FormattedMessage {...statLabel} />
                )}
              </StatLabel>
              <Score as="p" color="var(--c-nd-600)">
                {stat.statValue ? (
                  <FormattedNumber value={stat.statValue} />
                ) : (
                  '-'
                )}
              </Score>
            </ScoreRow>
          );
        })}
      </DetailsBlock>
    </Vertical>
  );
};

DecisiveScore.fragments = {
  playerGameScoreInterface: gql`
    fragment DecisiveScore_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      anyGame {
        id
        statusTyped
      }
      detailedScore {
        stat
        statValue
        category
      }
      ... on PlayerGameScore {
        id
        decisiveScore {
          totalScore
        }
      }
      ... on So5Score {
        id
        decisiveScore {
          totalScore
        }
      }
    }
  ` as TypedDocumentNode<DecisiveScore_playerGameScoreInterface>,
};
